import React from 'react';
import { useNavigate } from 'react-router-dom';
import Container from './Container.jsx';
import { FooterGrid, GridItem } from './ResponsiveGrid.jsx';
import { useTranslation } from '../../i18n/hooks';
import './Footer.css';

/**
 * Footer component with ArbiWin design system integration
 * Based on header_and_footer.html mockup footer structure
 *
 * @param {Object} props - Footer properties
 * @param {React.ReactNode} props.children - Footer content
 * @param {string} props.copyright - Copyright text
 * @param {Array} props.sections - Footer sections array
 * @param {Array} props.socialLinks - Social media links
 * @param {string} props.variant - Footer variant: 'default', 'minimal', 'detailed'
 * @param {string} props.className - Additional CSS classes
 * @param {string} props.testId - Test ID for testing
 * @returns {JSX.Element} Footer component
 */
const Footer = function Footer({
  children,
  copyright,
  sections = [],
  socialLinks = [],
  variant = 'default',
  className = '',
  testId
}) {

  const footerClasses = [
    'arbiwin-footer',
    'arbiwin-footer--' + variant,
    className
  ].filter(Boolean).join(' ');

  const currentYear = new Date().getFullYear();
  const defaultCopyright = copyright || `© ${currentYear} ArbiWin.bg. Всички права запазени.`;

  return (
    <footer
      className={footerClasses}
      data-testid={testId || 'arbiwin-footer'}
    >
      <Container size="full" padding="large">
        {children ? (
          children
        ) : (
          <>
            {sections.length > 0 && (
              <div className="arbiwin-footer__content">
                <FooterGrid>
                  {sections.map(function(section, index) {
                    return (
                      <GridItem key={index} align="start" justify="start">
                        <FooterSection
                          title={section.title}
                          links={section.links}
                          content={section.content}
                        />
                      </GridItem>
                    );
                  })}
                </FooterGrid>
              </div>
            )}

            {(defaultCopyright || socialLinks.length > 0) && (
              <div className="arbiwin-footer__bottom">
                {socialLinks.length > 0 && (
                  <div className="arbiwin-footer__social">
                    {socialLinks.map(function(link, index) {
                      return (
                        <a
                          key={index}
                          href={link.href}
                          className="arbiwin-footer__social-link"
                          target="_blank"
                          rel="noopener noreferrer"
                          aria-label={link.label}
                        >
                          {link.icon}
                        </a>
                      );
                    })}
                  </div>
                )}

                {defaultCopyright && (
                  <div className="arbiwin-footer__copyright">
                    <p>{defaultCopyright}</p>
                  </div>
                )}
              </div>
            )}
          </>
        )}
      </Container>
    </footer>
  );
};

/**
 * FooterSection component for individual footer sections
 *
 * @param {Object} props - FooterSection properties
 * @param {string} props.title - Section title
 * @param {Array} props.links - Section links array
 * @param {React.ReactNode} props.content - Custom section content
 * @param {string} props.className - Additional CSS classes
 * @returns {JSX.Element} FooterSection component
 */
const FooterSection = function FooterSection({
  title,
  links = [],
  content,
  className = ''
}) {

  const sectionClasses = [
    'arbiwin-footer-section',
    className
  ].filter(Boolean).join(' ');

  return (
    <div className={sectionClasses}>
      {title && (
        <h3 className="arbiwin-footer-section__title">
          {title}
        </h3>
      )}

      {content ? (
        <div className="arbiwin-footer-section__content">
          {content}
        </div>
      ) : (
        links.length > 0 && (
          <ul className="arbiwin-footer-section__list">
            {links.map(function(link, index) {
              return (
                <li key={index} className="arbiwin-footer-section__item">
                  <a
                    href={link.href}
                    className="arbiwin-footer-section__link"
                    onClick={link.onClick}
                    target={link.external ? '_blank' : undefined}
                    rel={link.external ? 'noopener noreferrer' : undefined}
                  >
                    {link.label}
                  </a>
                </li>
              );
            })}
          </ul>
        )
      )}
    </div>
  );
};

/**
 * ArbiWinFooter component - Pre-configured footer for ArbiWin
 * Based on the exact structure from header_and_footer.html
 *
 * @param {Object} props - ArbiWinFooter properties
 * @param {string} props.className - Additional CSS classes
 * @returns {JSX.Element} ArbiWinFooter component
 */
const ArbiWinFooter = function ArbiWinFooter({
  className = ''
}) {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const handleLinkClick = (e, path) => {
    e.preventDefault();
    
    // If path contains a hash (anchor link), handle scrolling
    if (path.includes('#')) {
      const [route, hash] = path.split('#');
      
      // Navigate to the route first
      navigate(route);
      
      // Then scroll to the element after a short delay to ensure page is loaded
      setTimeout(() => {
        const element = document.getElementById(hash);
        if (element) {
          // Expand the section if it's collapsible (check if toggle button exists and section is collapsed)
          const toggleButton = element.querySelector('.educational-section__toggle');
          if (toggleButton && !toggleButton.classList.contains('expanded')) {
            // Section is collapsed, expand it first
            toggleButton.click();
            // Wait a bit for the content to expand, then scroll
            setTimeout(() => {
              element.scrollIntoView({ behavior: 'smooth', block: 'start' });
            }, 100);
          } else {
            // Section is already expanded or not collapsible, just scroll
            element.scrollIntoView({ behavior: 'smooth', block: 'start' });
          }
        }
      }, 300);
    } else {
      navigate(path);
    }
  };

  const sections = [
    {
      title: 'ArbiWin.bg',
      content: (
        <p className="arbiwin-footer__description">
          {t('footer.description')}
        </p>
      )
    },
    {
      title: t('footer.quickLinks'),
      links: [
        { label: t('footer.terms'), href: '/static/terms', onClick: (e) => handleLinkClick(e, '/static/terms') },
        { label: t('footer.privacy'), href: '/static/privacy', onClick: (e) => handleLinkClick(e, '/static/privacy') },
        { label: t('footer.contact'), href: '/static/contact', onClick: (e) => handleLinkClick(e, '/static/contact') },
        { label: t('footer.cookieSettings'), href: '#', onClick: (e) => { e.preventDefault(); window.dispatchEvent(new CustomEvent('cookie-consent:openSettings')); } }
      ]
    },
    {
      title: t('footer.learnMore'),
      links: [
        { label: t('footer.about'), href: '/static/about', onClick: (e) => handleLinkClick(e, '/static/about') },
        { label: t('footer.howToSubscribe'), href: '/static/subscribe', onClick: (e) => handleLinkClick(e, '/static/subscribe') },
        { label: t('footer.faq'), href: '/#what-is-surebet', onClick: (e) => handleLinkClick(e, '/#what-is-surebet') }
      ]
    }
  ];

  return (
    <Footer
      sections={sections}
      copyright={t('footer.copyright')}
      className={'arbiwin-footer--arbiwin ' + className}
    />
  );
};

/**
 * MinimalFooter component - Simple footer with just copyright
 *
 * @param {Object} props - MinimalFooter properties
 * @param {string} props.copyright - Copyright text
 * @param {string} props.className - Additional CSS classes
 * @returns {JSX.Element} MinimalFooter component
 */
const MinimalFooter = function MinimalFooter({
  copyright,
  className = ''
}) {
  return (
    <Footer
      variant="minimal"
      copyright={copyright}
      className={'arbiwin-footer--minimal ' + className}
    />
  );
};

/**
 * DashboardFooter component - Footer for dashboard pages
 *
 * @param {Object} props - DashboardFooter properties
 * @param {string} props.className - Additional CSS classes
 * @returns {JSX.Element} DashboardFooter component
 */
const DashboardFooter = function DashboardFooter({
  className = ''
}) {

  const sections = [
    {
      title: 'Support',
      links: [
        { label: 'Help Center', href: '/help' },
        { label: 'Contact Support', href: '/support' },
        { label: 'Report Issue', href: '/report' }
      ]
    },
    {
      title: 'Account',
      links: [
        { label: 'Profile Settings', href: '/settings' },
        { label: 'Subscription', href: '/subscription' },
        { label: 'Billing', href: '/billing' }
      ]
    }
  ];

  return (
    <Footer
      sections={sections}
      variant="minimal"
      copyright="© 2026 ArbiWin.bg. Всички права запазени."
      className={'arbiwin-footer--dashboard ' + className}
    />
  );
};

Footer.displayName = 'Footer';
FooterSection.displayName = 'FooterSection';
ArbiWinFooter.displayName = 'ArbiWinFooter';
MinimalFooter.displayName = 'MinimalFooter';
DashboardFooter.displayName = 'DashboardFooter';

export default Footer;
export { FooterSection, ArbiWinFooter, MinimalFooter, DashboardFooter };
